import { CardWithActions, StyledButton } from "@/components";
import { AddIcon, DeleteIcon, EditIcon, SettingsIcon } from "@/icons";
import React, { useMemo, useState } from "react";
import CommonDataTable from "@/components/dataTableComponents/NewDatatable";
import DialogForm from "@/components/modal/DialogForm";
import { Form, Formik } from "formik";
import { Box, Grid, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import * as Yup from "yup";
import FormikCKEditor from "@/components/forms/FormikCKEditor";
import { useEmployeesUnderRMQuery } from "@/api/services/employeeList/EmployeeList";
import {
  useAddRecognizedDataMutation,
  useDeleteRecognizedDataMutation,
  useGetRecognizedDataQuery,
  useUpdateRecognizedDataMutation,
} from "@/api/services/recognized/RecognizedSlice";
import FormikMultipleSelectChip from "@/components/forms/FormikMultipleSelectChip";
import { toast } from "@/components/toastify";
import mapOptions from "@/utils/mappers/mapOptions";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useFetchActiveEmployeesQuery } from "@/api/services/adminEvents/events/adminEvents";
import useFetchCommonData from "@/hooks/useFetchCommonData";

dayjs.extend(isSameOrBefore);

const getUserDetails = () => {
  try {
    const data = JSON.parse(localStorage.getItem("loginDetails"));
    return data?.userDetails?.empId || "";
  } catch {
    return "";
  }
};

const Recognized = () => {
  const [editData, setEditData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);

  const empId = useMemo(() => getUserDetails(), []);
  const { data: recognizedData = [] } = useGetRecognizedDataQuery();
  // const { data: fetchEmployeesUnderReportingManager = [] } =
  //   useEmployeesUnderRMQuery(empId);

  const { data: activeEmployees = [] } = useFetchActiveEmployeesQuery();

  const { data: fetchAllEmployees } = useFetchCommonData("EMPLOYEES");

  const [addRecognizedData, { isLoading: addLoading }] =
    useAddRecognizedDataMutation();
  const [updateRecognizedData, { isLoading: updateLoading }] =
    useUpdateRecognizedDataMutation();
  const [deleteRecognizedData, { isLoading: deleteLoading }] =
    useDeleteRecognizedDataMutation();

  const handleOpenModal = (row = null) => {
    if (row) {
      setEditMode(true);
      setEditData(row);
    } else {
      setEditMode(false);
      setEditData(null);
    }
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditMode(false);
    setEditData(null);
  };

  const handleDelete = async (row) => {
    try {
      await deleteRecognizedData(row.id).unwrap();
      toast("success", "Recognition deleted successfully!");
    } catch (error) {
      toast("error", "Failed to delete recognition.");
    }
  };

  const getInitialValues = () => ({
    empList: editData?.empList || [],
    from: editData?.fromDate ? new Date(editData.fromDate) : "",
    to: editData?.toDate ? new Date(editData.toDate) : "",
    description: editData?.description || "",
  });

  const validationSchema = Yup.object().shape({
    empList: Yup.array()
      .of(Yup.string())
      .min(1, "At least one employee must be selected")
      .required("Please select at least one employee"),
    from: Yup.date().required("From Date is required"),
    to: Yup.date()
      .required("To Date is required")
      .min(Yup.ref("from"), "To Date must be after From Date"),
    description: Yup.string().required("Description is required"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    console.log(values, "values");

    const payload = {
      empList: values.empList,
      from: values.from,
      to: values.to,
      description: values.description,
    };
    console.log(editData, "edit");
    try {
      if (editMode && editData) {
        await updateRecognizedData({ id: editData.id, payload }).unwrap();
        toast("success", "Recognition updated successfully!");
      } else {
        await addRecognizedData(payload).unwrap();
        toast("success", "Recognition submitted successfully!");
      }

      resetForm();
      handleCloseModal();
    } catch (error) {
      toast("error", error?.data?.message || "Submission failed");
    }
  };

  const rows = recognizedData.map((item) => ({
    id: item.id,
    empList: item.empList,
    to: item.empList.join(", "),
    fromDate: item.from,
    toDate: item.to,
    description: item.description,
  }));

  const columns = [
    { field: "to", headerName: "To" },
    // {
    //   field: "empList",
    //   headerName: "Employees",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <Stack direction="row" spacing={1} flexWrap="wrap">
    //       {params.row.empList.map((emp, index) => (
    //         <Chip key={index} label={emp} size="small" color="primary" />
    //       ))}
    //     </Stack>
    //   ),
    // },
    { field: "toDate", headerName: "To Date" },
    { field: "fromDate", headerName: "From Date" },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params) => (
        <div
          dangerouslySetInnerHTML={{ __html: params.row.description }}
          style={{ maxHeight: 100, overflowY: "auto" }}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params) => {
        const rowData = params.row;
        return (
          <ActionButtons
            row={rowData}
            onEdit={() => handleOpenModal(rowData)}
            onDelete={() => handleDelete(rowData)}
          />
        );
      },
    },
  ];

  const ActionButtons = ({ row, onEdit, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openAction = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        <Tooltip title="Settings">
          <IconButton onClick={handleClick} size="small">
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={openAction} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              onEdit();
              handleClose();
            }}
          >
            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDelete();
              handleClose();
            }}
          >
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
            Delete
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <>
      <CardWithActions
        headerTitle="Recognition"
        headerActions={[
          <StyledButton
            icon={<AddIcon />}
            label="Recognition"
            onClick={() => handleOpenModal()}
          />,
        ]}
      >
        <CommonDataTable columns={columns} rows={rows} searchBar search />
      </CardWithActions>

      <DialogForm
        title={editMode ? "Edit Recognition" : "Add Recognition"}
        open={open}
        setOpen={setOpen}
        width="sm"
      >
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikMultipleSelectChip
                    search
                    name="empList"
                    label="Select Employee"
                    options={mapOptions(fetchAllEmployees)}
                    placeholder="Select Employee"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormikDatePicker
                    required
                    name="from"
                    label="From Date"
                    views={["year", "month", "day"]}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormikDatePicker
                    required
                    name="to"
                    label="To Date"
                    views={["year", "month", "day"]}
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    // disabled={!values?.from}
                    shouldDisableDate={(currentDate) => {
                      const fromDate = values?.from;
                      if (!fromDate) return true;
                      return dayjs(currentDate).isBefore(
                        dayjs(fromDate),
                        "day"
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormikCKEditor
                    name="description"
                    label="Description"
                    required
                    height="180px"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <StyledButton
                      type="reset"
                      label="Reset"
                      variant="outlined"
                      disabled={editMode ? updateLoading : addLoading}
                    />
                    <StyledButton
                      isSubmitting={editMode ? updateLoading : addLoading}
                      type="submit"
                      label={editMode ? "Save Changes" : "Submit"}
                      variant="contained"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogForm>
    </>
  );
};

export default Recognized;
